/**
 * Passos 1 a 4 do onboarding: cliente, hospital, conexao e dataset.
 *
 * A ordem nao e estetica - cada passo depende do anterior, e o backend recusa a inversao. Por isso
 * cada passo, quando nao pode ser executado, diz QUAL passo falta em vez de mostrar um formulario
 * que vai dar erro.
 */

import { Button } from '@ledger-glass/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useId, useState } from 'react'

import { fetchClients, fetchConnections, fetchDatasets, fetchHospitals } from '../api/catalog'
import {
  criarCliente,
  criarConexao,
  criarDataset,
  criarHospital,
  type Snapshot,
} from '../api/onboarding'
import type { Client } from '../api/types'
import { Alert } from '../components/Alert'
import { humanizar } from '../errors/humanizar'
import { CampoSelecao, CampoTexto } from './campos'

export interface PassoProps {
  snapshot: Snapshot
  tenantId: string
  cliente: Client | undefined
}

/** Recado padrao de dependencia: some o formulario e fica a instrucao. */
function Falta({ children }: { children: React.ReactNode }) {
  return <Alert tone="warning">{children}</Alert>
}

// ------------------------------------------------------------------ 1. cliente

export function PassoCliente({ snapshot, tenantId, cliente }: PassoProps) {
  const queryClient = useQueryClient()
  const mutacao = useMutation({
    mutationFn: () => criarCliente(tenantId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['clients'] })
      await queryClient.invalidateQueries({ queryKey: ['summary'] })
    },
  })

  return (
    <>
      <p className="pagina__descricao">
        O cliente é o espelho do <strong>Tenant</strong> do Auth dentro do Report Manager. Tudo o
        que vem depois — hospital, conexão, dataset, relatório, política — pertence a um cliente.
      </p>
      <p className="passo__contexto">
        Tenant da sua sessão: <strong>{snapshot.tenant.name ?? tenantId}</strong>{' '}
        <code>{tenantId}</code>
      </p>

      {cliente !== undefined ? (
        <Alert tone="positive">
          Tenant já mapeado como cliente: <strong>{cliente.name}</strong>.
        </Alert>
      ) : (
        <>
          {mutacao.error === null ? null : <Alert tone="danger">{humanizar(mutacao.error)}</Alert>}
          <Button disabled={mutacao.isPending} onClick={() => mutacao.mutate()}>
            {mutacao.isPending ? 'Mapeando…' : 'Mapear este tenant como cliente'}
          </Button>
        </>
      )}
    </>
  )
}

// ----------------------------------------------------------------- 2. hospital

export function PassoHospital({ snapshot, cliente }: PassoProps) {
  const id = useId()
  const queryClient = useQueryClient()
  const hospitais = useQuery({ queryKey: ['hospitals'], queryFn: fetchHospitals })
  const [organizacao, setOrganizacao] = useState('')

  const mutacao = useMutation({
    mutationFn: () => criarHospital(cliente?.id ?? '', organizacao),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['hospitals'] })
      await queryClient.invalidateQueries({ queryKey: ['summary'] })
      setOrganizacao('')
    },
  })

  if (cliente === undefined) return <Falta>Faça o passo 1 antes: o tenant ainda não é cliente.</Falta>

  const jaMapeadas = new Set((hospitais.data ?? []).map((item) => item.auth_organization_id))
  const disponiveis = snapshot.organizations.filter((item) => !jaMapeadas.has(item.id))

  return (
    <>
      <p className="pagina__descricao">
        Espelha uma <strong>Organization</strong> do Auth — hospital, plano ou área. Serve para
        agrupar relatórios e para políticas que filtram por organização. É opcional: um cliente pode
        ter relatórios sem nenhuma organização.
      </p>

      {snapshot.organizations.length === 0 ? (
        <Falta>
          Este tenant não tem nenhuma Organization no Auth. Ela é criada lá, no Auth Manager — aqui
          só se mapeia.
        </Falta>
      ) : disponiveis.length === 0 ? (
        <Alert tone="positive">Todas as organizations deste tenant já estão mapeadas.</Alert>
      ) : (
        <form
          className="formulario"
          onSubmit={(evento) => {
            evento.preventDefault()
            if (organizacao !== '') mutacao.mutate()
          }}
        >
          <CampoSelecao
            aoMudar={setOrganizacao}
            dica="Vem do Auth. Só aparecem as que ainda não foram mapeadas."
            id={`${id}-org`}
            obrigatorio
            opcoes={disponiveis.map((item) => ({ valor: item.id, rotulo: item.name }))}
            rotulo="Organization (Auth)"
            valor={organizacao}
            vazio="— escolha —"
          />
          {mutacao.error === null ? null : <Alert tone="danger">{humanizar(mutacao.error)}</Alert>}
          {mutacao.isSuccess ? <Alert tone="positive">Hospital mapeado.</Alert> : null}
          <div>
            <Button disabled={organizacao === '' || mutacao.isPending} type="submit">
              {mutacao.isPending ? 'Mapeando…' : 'Mapear hospital'}
            </Button>
          </div>
        </form>
      )}
    </>
  )
}

// ----------------------------------------------------------------- 3. conexao

const CONEXAO_VAZIA = {
  name: '',
  connection_key: '',
  microsoft_tenant_id: '',
  entra_client_id: '',
  secret_ref: '',
}

export function PassoConexao({ cliente }: PassoProps) {
  const id = useId()
  const queryClient = useQueryClient()
  const [campos, setCampos] = useState(CONEXAO_VAZIA)

  const mutacao = useMutation({
    mutationFn: () => criarConexao({ client_id: cliente?.id ?? '', ...campos }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['powerbi-connections'] })
      await queryClient.invalidateQueries({ queryKey: ['summary'] })
      setCampos(CONEXAO_VAZIA)
    },
  })

  if (cliente === undefined) return <Falta>Faça o passo 1 antes: o tenant ainda não é cliente.</Falta>

  function muda(chave: keyof typeof CONEXAO_VAZIA) {
    return (valor: string) => setCampos((atual) => ({ ...atual, [chave]: valor }))
  }

  const completo = Object.values(campos).every((valor) => valor.trim() !== '')

  return (
    <>
      <p className="pagina__descricao">
        As credenciais do <strong>service principal (Entra)</strong> com que falamos com o Power BI.
        O segredo em si <strong>não é digitado aqui</strong>: guarda-se apenas uma referência, e o
        valor real vive no ambiente do backend.
      </p>

      <form
        className="formulario"
        onSubmit={(evento) => {
          evento.preventDefault()
          if (completo) mutacao.mutate()
        }}
      >
        <CampoTexto
          aoMudar={muda('name')}
          dica="Rótulo livre, para você identificar."
          exemplo="SYN Saude - PowerBI"
          id={`${id}-nome`}
          obrigatorio
          rotulo="Nome"
          valor={campos.name}
        />
        <CampoTexto
          aoMudar={muda('connection_key')}
          dica="Identificador técnico (minúsculas, números, _ e -). Use a MESMA chave no POWERBI_CONNECTIONS_JSON do backend, senão a conexão não autentica."
          exemplo="synsaude"
          id={`${id}-key`}
          obrigatorio
          rotulo="connection_key"
          valor={campos.connection_key}
        />
        <CampoTexto
          aoMudar={muda('microsoft_tenant_id')}
          dica="Directory (tenant) ID: Azure Portal → Microsoft Entra ID → Overview."
          id={`${id}-tenant`}
          obrigatorio
          rotulo="Microsoft Entra Tenant ID"
          valor={campos.microsoft_tenant_id}
        />
        <CampoTexto
          aoMudar={muda('entra_client_id')}
          dica="Application (client) ID: Azure Portal → App registrations → seu app → Overview."
          id={`${id}-client`}
          obrigatorio
          rotulo="Entra Client ID"
          valor={campos.entra_client_id}
        />
        <CampoTexto
          aoMudar={muda('secret_ref')}
          dica="Um NOME que aponta para o segredo, nunca o segredo. O valor real vai no ambiente do backend."
          id={`${id}-secret`}
          obrigatorio
          rotulo="Referência do secret"
          valor={campos.secret_ref}
        />

        {mutacao.error === null ? null : <Alert tone="danger">{humanizar(mutacao.error)}</Alert>}
        {mutacao.isSuccess ? <Alert tone="positive">Conexão criada.</Alert> : null}
        <div>
          <Button disabled={!completo || mutacao.isPending} type="submit">
            {mutacao.isPending ? 'Criando…' : 'Criar conexão'}
          </Button>
        </div>
      </form>
    </>
  )
}

// ----------------------------------------------------------------- 4. dataset

export function PassoDataset({ cliente }: PassoProps) {
  const id = useId()
  const queryClient = useQueryClient()
  const conexoes = useQuery({ queryKey: ['powerbi-connections'], queryFn: fetchConnections })
  const [conexao, setConexao] = useState('')
  const [campos, setCampos] = useState({
    name: '',
    powerbi_workspace_id: '',
    powerbi_dataset_id: '',
    description: '',
  })

  /**
   * A conexao EFETIVA, nao a escolhida: com uma conexao so, o `select` ja mostra ela mas o estado
   * comeca vazio. Sem isto, criar sem tocar no campo mandava `powerbi_connection_id` vazio e o
   * backend recusava algo que a tela dizia estar preenchido.
   */
  const doCliente = (conexoes.data ?? []).filter((item) => item.client_id === cliente?.id)
  const escolhida = conexao === '' ? (doCliente[0]?.id ?? '') : conexao

  const mutacao = useMutation({
    mutationFn: () =>
      criarDataset({
        client_id: cliente?.id ?? '',
        powerbi_connection_id: escolhida,
        name: campos.name,
        powerbi_workspace_id: campos.powerbi_workspace_id,
        powerbi_dataset_id: campos.powerbi_dataset_id,
        description: campos.description === '' ? null : campos.description,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['datasets'] })
      await queryClient.invalidateQueries({ queryKey: ['summary'] })
      setCampos({ name: '', powerbi_workspace_id: '', powerbi_dataset_id: '', description: '' })
    },
  })

  if (cliente === undefined) return <Falta>Faça o passo 1 antes: o tenant ainda não é cliente.</Falta>

  if (doCliente.length === 0) {
    return <Falta>Este cliente ainda não tem conexão Power BI. Faça o passo 3.</Falta>
  }

  function muda(chave: keyof typeof campos) {
    return (valor: string) => setCampos((atual) => ({ ...atual, [chave]: valor }))
  }

  const completo =
    escolhida !== '' &&
    campos.name.trim() !== '' &&
    campos.powerbi_workspace_id.trim() !== '' &&
    campos.powerbi_dataset_id.trim() !== ''

  return (
    <>
      <p className="pagina__descricao">
        O <strong>semantic model</strong> do Power BI: onde ficam os dados e as <em>roles</em> de
        RLS. Um relatório aponta para um dataset, e é o dataset que o token de embed carrega.
      </p>
      <Alert tone="info">
        No passo 5 existe <strong>Buscar no Power BI</strong>, que cria o dataset sozinho a partir do
        link do relatório — com o dataset id real, em vez de digitado. Este passo serve para quando
        você já tem os ids em mãos ou quer cadastrar o dataset antes do relatório.
      </Alert>

      <form
        className="formulario"
        onSubmit={(evento) => {
          evento.preventDefault()
          if (completo) mutacao.mutate()
        }}
      >
        <CampoSelecao
          aoMudar={setConexao}
          dica="Define quem fala com o Power BI. Só as conexões deste cliente."
          id={`${id}-conn`}
          obrigatorio
          opcoes={doCliente.map((item) => ({ valor: item.id, rotulo: item.name }))}
          rotulo="Conexão Power BI"
          valor={escolhida}
        />
        <CampoTexto
          aoMudar={muda('name')}
          dica="Rótulo livre, para identificação."
          exemplo="Comercial"
          id={`${id}-nome`}
          obrigatorio
          rotulo="Nome do dataset"
          valor={campos.name}
        />
        <CampoTexto
          aoMudar={muda('powerbi_workspace_id')}
          dica="groupId do workspace: sai do link do relatório, entre /groups/ e /reports/."
          id={`${id}-ws`}
          obrigatorio
          rotulo="PowerBI Workspace ID (groupId)"
          valor={campos.powerbi_workspace_id}
        />
        <CampoTexto
          aoMudar={muda('powerbi_dataset_id')}
          dica="GUID do semantic model, nas configurações do dataset no Power BI."
          id={`${id}-ds`}
          obrigatorio
          rotulo="PowerBI Dataset ID"
          valor={campos.powerbi_dataset_id}
        />
        <CampoTexto
          aoMudar={muda('description')}
          id={`${id}-desc`}
          rotulo="Descrição (opcional)"
          valor={campos.description}
        />

        {mutacao.error === null ? null : <Alert tone="danger">{humanizar(mutacao.error)}</Alert>}
        {mutacao.isSuccess ? <Alert tone="positive">Dataset criado.</Alert> : null}
        <div>
          <Button disabled={!completo || mutacao.isPending} type="submit">
            {mutacao.isPending ? 'Criando…' : 'Criar dataset'}
          </Button>
        </div>
      </form>
    </>
  )
}

/** Usado pela pagina para saber se o passo ja esta cumprido (marca no menu de passos). */
export function usePassosCumpridos() {
  const clientes = useQuery({ queryKey: ['clients'], queryFn: fetchClients })
  const hospitais = useQuery({ queryKey: ['hospitals'], queryFn: fetchHospitals })
  const conexoes = useQuery({ queryKey: ['powerbi-connections'], queryFn: fetchConnections })
  const datasets = useQuery({ queryKey: ['datasets'], queryFn: fetchDatasets })
  return {
    clientes: clientes.data ?? [],
    temCliente: (clientes.data ?? []).length > 0,
    temHospital: (hospitais.data ?? []).length > 0,
    temConexao: (conexoes.data ?? []).length > 0,
    temDataset: (datasets.data ?? []).length > 0,
    carregando: clientes.isPending,
    erro: clientes.error,
  }
}
